import { useState, useEffect } from "react";
import { getDashboard, getGoals } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  TrendingUp, 
  TrendingDown, 
  Minus,
  ArrowRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Award,
  Target,
  Printer,
  BarChart3
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  LineChart,
  Line
} from "recharts";

const YEARS = [2024, 2025, 2026, 2027];

export default function YearComparison() {
  const [year1, setYear1] = useState(2025); // Previous year
  const [year2, setYear2] = useState(2026); // Current year
  const [data1, setData1] = useState(null);
  const [data2, setData2] = useState(null);
  const [goals1, setGoals1] = useState(null);
  const [goals2, setGoals2] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [d1, d2, g1, g2] = await Promise.all([
          getDashboard(year1),
          getDashboard(year2),
          getGoals(year1),
          getGoals(year2)
        ]);
        setData1(d1);
        setData2(d2);
        setGoals1(g1);
        setGoals2(g2);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year1, year2]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl mx-auto">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const rates1 = data1?.ytd_rates || {};
  const rates2 = data2?.ytd_rates || {};
  const stats1 = data1?.ytd_stats || {};
  const stats2 = data2?.ytd_stats || {};

  // Calculate changes and improvements
  const calculateChange = (val1, val2, inverse = false) => {
    if (val1 === undefined || val2 === undefined || val1 === 0) return { change: 0, trend: "same", improved: false };
    const change = ((val2 - val1) / val1) * 100;
    const trend = change > 5 ? "up" : change < -5 ? "down" : "same";
    // For rates (lower is better unless inverse), down is improvement
    // For compliance (inverse=true, higher is better), up is improvement
    const improved = inverse ? change > 0 : change < 0;
    return { change: Math.abs(change).toFixed(1), trend, improved };
  };

  // Check if goal was met
  const goalMet = (value, goal, inverse = false) => {
    if (value === undefined || goal === undefined) return null;
    return inverse ? value >= goal : value <= goal;
  };

  // Define metrics for comparison
  const metrics = [
    { key: "cauti_rate", label: "CAUTI Rate", unit: "/1K cath days", goalKey: "cauti_rate" },
    { key: "clabsi_rate", label: "CLABSI Rate", unit: "/1K line days", goalKey: "clabsi_rate" },
    { key: "cdiff_rate", label: "C.diff Rate", unit: "/10K pt days", goalKey: "cdiff_rate" },
    { key: "total_hai_rate", label: "Total HAI Rate", unit: "/1K pt days", goalKey: "total_hai_rate" },
    { key: "mdro_rate", label: "MDRO Rate", unit: "/1K pt days", goalKey: "mdro_rate" },
    { key: "hand_hygiene_rate", label: "Hand Hygiene", unit: "%", goalKey: "hand_hygiene_compliance", inverse: true },
    { key: "isolation_ppe_rate", label: "Isolation PPE", unit: "%", goalKey: "isolation_ppe_compliance", inverse: true },
  ];

  // Build comparison data for charts
  const comparisonData = metrics.map(m => ({
    metric: m.label,
    [year1]: rates1[m.key] || 0,
    [year2]: rates2[m.key] || 0,
    goal1: goals1?.[m.goalKey] || 0,
    goal2: goals2?.[m.goalKey] || 0,
  }));

  // Categorize metrics by performance
  const improvements = [];
  const declines = [];
  const maintained = [];

  metrics.forEach(m => {
    const val1 = rates1[m.key];
    const val2 = rates2[m.key];
    const { trend, improved, change } = calculateChange(val1, val2, m.inverse);
    const met1 = goalMet(val1, goals1?.[m.goalKey], m.inverse);
    const met2 = goalMet(val2, goals2?.[m.goalKey], m.inverse);
    
    const item = {
      ...m,
      val1, val2, change, trend, improved, met1, met2,
      goal1: goals1?.[m.goalKey],
      goal2: goals2?.[m.goalKey]
    };
    
    if (trend === "same") {
      maintained.push(item);
    } else if (improved) {
      improvements.push(item);
    } else {
      declines.push(item);
    }
  });

  // Radar chart data for compliance comparison
  const radarData = [
    { metric: "Hand Hygiene", [year1]: rates1.hand_hygiene_rate || 0, [year2]: rates2.hand_hygiene_rate || 0 },
    { metric: "Isolation PPE", [year1]: rates1.isolation_ppe_rate || 0, [year2]: rates2.isolation_ppe_rate || 0 },
    { metric: "IC EOC", [year1]: rates1.ic_eoc_rate || 0, [year2]: rates2.ic_eoc_rate || 0 },
    { metric: "Dialysis EOC", [year1]: rates1.dialysis_eoc_rate || 0, [year2]: rates2.dialysis_eoc_rate || 0 },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Year-over-Year Analysis
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Compare performance between years for committee presentation
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Select value={String(year1)} onValueChange={(v) => setYear1(Number(v))}>
              <SelectTrigger className="w-24" data-testid="year1-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-slate-400">vs</span>
            <Select value={String(year2)} onValueChange={(v) => setYear2(Number(v))}>
              <SelectTrigger className="w-24" data-testid="year2-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEARS.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handlePrint} variant="outline" data-testid="print-comparison-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block mb-6">
        <div className="border-b-2 border-[#6A26B5] pb-3">
          <h1 className="text-xl font-bold text-[#6A26B5]">Year-over-Year Performance Analysis</h1>
          <p className="text-slate-600">Encompass Health Fitchburg • {year1} vs {year2}</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-[#6A26B5]">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Patient Days ({year1})</p>
            <p className="text-2xl font-bold text-[#6A26B5] font-mono">{stats1.patient_days?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Patient Days ({year2})</p>
            <p className="text-2xl font-bold text-cyan-600 font-mono">{stats2.patient_days?.toLocaleString() || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Improvements</p>
            <p className="text-2xl font-bold text-emerald-600">{improvements.length}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Areas to Address</p>
            <p className="text-2xl font-bold text-red-600">{declines.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Improvements Section */}
      {improvements.length > 0 && (
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="w-5 h-5" />
              Areas of Improvement ({year1} → {year2})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {improvements.map((item, i) => (
                <div key={i} className="p-4 hover:bg-emerald-50/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{item.label}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="text-slate-500">
                          {year1}: <span className="font-mono">{item.val1?.toFixed(2) || '-'}{item.unit}</span>
                          {item.met1 !== null && (
                            item.met1 ? 
                              <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" /> :
                              <XCircle className="w-3 h-3 inline ml-1 text-red-500" />
                          )}
                        </span>
                        <ArrowRight className="w-4 h-4 text-emerald-500" />
                        <span className="text-emerald-700 font-medium">
                          {year2}: <span className="font-mono">{item.val2?.toFixed(2) || '-'}{item.unit}</span>
                          {item.met2 !== null && (
                            item.met2 ? 
                              <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" /> :
                              <XCircle className="w-3 h-3 inline ml-1 text-red-500" />
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <TrendingDown className="w-3 h-3 mr-1" />
                        {item.change}% {item.inverse ? 'increase' : 'decrease'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Declines / Areas Needing Attention */}
      {declines.length > 0 && (
        <Card className="shadow-lg border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5" />
              Areas Needing Attention ({year1} → {year2})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {declines.map((item, i) => (
                <div key={i} className="p-4 hover:bg-red-50/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{item.label}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm">
                        <span className="text-slate-500">
                          {year1}: <span className="font-mono">{item.val1?.toFixed(2) || '-'}{item.unit}</span>
                          {item.met1 !== null && (
                            item.met1 ? 
                              <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" /> :
                              <XCircle className="w-3 h-3 inline ml-1 text-red-500" />
                          )}
                        </span>
                        <ArrowRight className="w-4 h-4 text-red-500" />
                        <span className="text-red-700 font-medium">
                          {year2}: <span className="font-mono">{item.val2?.toFixed(2) || '-'}{item.unit}</span>
                          {item.met2 !== null && (
                            item.met2 ? 
                              <CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-500" /> :
                              <XCircle className="w-3 h-3 inline ml-1 text-red-500" />
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-100 text-red-700">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        {item.change}% {item.inverse ? 'decrease' : 'increase'}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Data Warning */}
      {!data1?.months_with_data && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">No data for {year1}</p>
              <p className="text-sm text-amber-700">
                Go to Data Import to upload {year1} data from your Excel scorecard
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison Bar Chart */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <BarChart3 className="w-5 h-5 text-[#6A26B5]" />
            Infection Rate Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={comparisonData.slice(0, 5)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="metric" type="category" tick={{ fontSize: 11 }} width={100} />
              <Tooltip 
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                formatter={(value) => value?.toFixed(2)}
              />
              <Legend />
              <Bar dataKey={year1} fill="#6A26B5" radius={[0, 4, 4, 0]} name={`${year1}`} />
              <Bar dataKey={year2} fill="#00B4D8" radius={[0, 4, 4, 0]} name={`${year2}`} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Compliance Radar Comparison */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Compliance Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar name={`${year1}`} dataKey={year1} stroke="#6A26B5" fill="#6A26B5" fillOpacity={0.3} strokeWidth={2} />
              <Radar name={`${year2}`} dataKey={year2} stroke="#00B4D8" fill="#00B4D8" fillOpacity={0.3} strokeWidth={2} />
              <Legend />
              <Tooltip formatter={(value) => `${value?.toFixed(1)}%`} />
            </RadarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Goal Achievement Summary */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Target className="w-5 h-5 text-[#6A26B5]" />
            Goal Achievement Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-medium">Metric</th>
                  <th className="text-center p-3 font-medium">{year1} Goal</th>
                  <th className="text-center p-3 font-medium">{year1} Actual</th>
                  <th className="text-center p-3 font-medium">{year1} Status</th>
                  <th className="text-center p-3 font-medium">{year2} Goal</th>
                  <th className="text-center p-3 font-medium">{year2} Actual</th>
                  <th className="text-center p-3 font-medium">{year2} Status</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, i) => {
                  const met1 = goalMet(rates1[m.key], goals1?.[m.goalKey], m.inverse);
                  const met2 = goalMet(rates2[m.key], goals2?.[m.goalKey], m.inverse);
                  return (
                    <tr key={i} className="border-b">
                      <td className="p-3 font-medium">{m.label}</td>
                      <td className="p-3 text-center font-mono text-slate-500">
                        {m.inverse ? '≥' : '≤'}{goals1?.[m.goalKey] ?? '-'}
                      </td>
                      <td className="p-3 text-center font-mono">{rates1[m.key]?.toFixed(2) ?? '-'}</td>
                      <td className="p-3 text-center">
                        {met1 === null ? '-' : met1 ? 
                          <Badge className="bg-emerald-100 text-emerald-700">Met</Badge> :
                          <Badge className="bg-red-100 text-red-700">Not Met</Badge>
                        }
                      </td>
                      <td className="p-3 text-center font-mono text-slate-500">
                        {m.inverse ? '≥' : '≤'}{goals2?.[m.goalKey] ?? '-'}
                      </td>
                      <td className="p-3 text-center font-mono">{rates2[m.key]?.toFixed(2) ?? '-'}</td>
                      <td className="p-3 text-center">
                        {met2 === null ? '-' : met2 ? 
                          <Badge className="bg-emerald-100 text-emerald-700">Met</Badge> :
                          <Badge className="bg-red-100 text-red-700">Not Met</Badge>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* HAI Counts Comparison */}
      <Card className="shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Infection Counts Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4">
            {[
              { label: "CAUTI", key: "cauti_count" },
              { label: "CLABSI", key: "clabsi_count" },
              { label: "C.diff", key: "cdiff_count" },
              { label: "MDRO", key: "mdro_count" },
              { label: "Total HAIs", key: "total_hai_count", isRate: true },
            ].map((item) => {
              const v1 = item.isRate ? rates1[item.key] : stats1[item.key];
              const v2 = item.isRate ? rates2[item.key] : stats2[item.key];
              const change = v1 && v2 ? v2 - v1 : 0;
              return (
                <div key={item.label} className="p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-500 mb-2">{item.label}</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-lg font-bold text-[#6A26B5] font-mono">{v1 || 0}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="text-lg font-bold text-cyan-600 font-mono">{v2 || 0}</span>
                  </div>
                  <p className={`text-xs mt-1 ${change < 0 ? 'text-emerald-600' : change > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                    {change > 0 ? '+' : ''}{change} from {year1}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Print Footer */}
      <div className="hidden print:block mt-8 pt-4 border-t">
        <p className="text-sm text-slate-500">
          Generated: {new Date().toLocaleDateString()} • Encompass Health Fitchburg • Year-over-Year Analysis
        </p>
      </div>
    </div>
  );
}
